import React, { useState } from 'react';
import { IAuditoriaCashRichResultado, IAuditoriaCashRichItem } from '../interfaces/IAuditoriaCashRich';

interface Props {
  resultado: IAuditoriaCashRichResultado;
}

const formatCurrency = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (v: number) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const ItemRow: React.FC<{ item: IAuditoriaCashRichItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr
        onClick={() => setExpandido(!expandido)}
        style={{ cursor: 'pointer', backgroundColor: '#fffbeb' }}
      >
        <td>{item.empresaNome}</td>
        <td>{item.periodoFechamento}</td>
        <td className='text-end'>{formatCurrency(item.saldoCaixaBancos)}</td>
        <td className='text-end'>{formatCurrency(item.saldoEmprestimos)}</td>
        <td className='text-end'>
          <span style={{ color: '#92400e', fontWeight: 600 }}>
            {formatCurrency(item.excessoCaixa)}
          </span>
        </td>
        <td className='text-end'>
          <span style={{ color: '#b45309' }}>{formatPct(item.percentualOciosidade)}</span>
        </td>
        <td className='text-end'>{formatCurrency(item.jurosEstimados)}</td>
        <td className='text-end'>
          <strong style={{ color: '#92400e' }}>{formatCurrency(item.impactoFiscal)}</strong>
        </td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={8} style={{ backgroundColor: '#f8fafc', padding: '1rem' }}>

            {/* Banner de diagnóstico */}
            <div
              className='alert mb-3 py-2'
              style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}
            >
              <small>{item.mensagemDiagnostico}</small>
            </div>

            {/* Saldos das contas — COR-287: drill-down por conta individual (não há um
                lançamento único "causador" nesta comparação de saldos de grupos de contas). */}
            <div className='row g-3 mb-3'>
              <div className='col-md-6'>
                <h6 className='text-muted mb-2'>Disponibilidades (Ativo) — {formatCurrency(item.saldoCaixaBancos)}</h6>
                <table className='table table-sm table-bordered mb-0'>
                  <tbody>
                    {(item.contasCaixa || []).length > 0 ? (
                      item.contasCaixa.map((conta, idx) => (
                        <tr key={`${conta.contaContabil}-${idx}`}>
                          <td><code>{conta.contaContabil}</code> {conta.descricaoConta}</td>
                          <td className='text-end'>{formatCurrency(conta.saldo)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td>Caixa (1.01.01.01) + Bancos (1.01.01.02)</td>
                          <td className='text-end'><strong>{formatCurrency(item.saldoCaixaBancos)}</strong></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className='col-md-6'>
                <h6 className='text-muted mb-2'>Passivo Oneroso (Dívida) — {formatCurrency(item.saldoEmprestimos)}</h6>
                <table className='table table-sm table-bordered mb-0'>
                  <tbody>
                    {(item.contasDivida || []).length > 0 ? (
                      item.contasDivida.map((conta, idx) => (
                        <tr key={`${conta.contaContabil}-${idx}`}>
                          <td><code>{conta.contaContabil}</code> {conta.descricaoConta}</td>
                          <td className='text-end'>{formatCurrency(conta.saldo)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td>Empréstimos CP (2.01.03.01) + LP (2.02.03.01)</td>
                          <td className='text-end'><strong>{formatCurrency(item.saldoEmprestimos)}</strong></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Memória de cálculo */}
            <div className='mb-2'>
              <h6 className='text-muted mb-2'>Memória de Cálculo — Impacto Estimado</h6>
              <table className='table table-sm table-bordered mb-0' style={{ maxWidth: 460 }}>
                <tbody>
                  <tr>
                    <td>Excesso de Caixa (Caixa − Dívida)</td>
                    <td className='text-end' style={{ color: '#92400e', fontWeight: 600 }}>
                      {formatCurrency(item.excessoCaixa)}
                    </td>
                  </tr>
                  <tr>
                    <td>% Ociosidade ((Caixa/Dívida − 1) × 100)</td>
                    <td className='text-end'>{formatPct(item.percentualOciosidade)}</td>
                  </tr>
                  <tr>
                    <td>Taxa SELIC Mensal (BCB Série 4189)</td>
                    <td className='text-end'>{formatPct(item.taxaSelicMensal)}</td>
                  </tr>
                  <tr>
                    <td>Juros Estimados (Excesso × SELIC / 100)</td>
                    <td className='text-end text-warning-emphasis'>{formatCurrency(item.jurosEstimados)}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#fef3c7' }}>
                    <td><strong>Impacto Fiscal Estimado (Juros × 34%)</strong></td>
                    <td className='text-end'>
                      <strong style={{ color: '#92400e' }}>{formatCurrency(item.impactoFiscal)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <small className='text-muted mt-1 d-block'>
                34% = IRPJ 25% (15% básico + 10% adicional) + CSLL 9% — alíquota de glosagem da despesa financeira.
              </small>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCashRichDetalhe: React.FC<Props> = ({ resultado }) => {
  const temAviso = resultado.totalAvisos > 0;

  return (
    <div className='auditoria-card'>
      <h4>Ineficiência Financeira — Cash-Rich com Dívida Cara</h4>
      <p className='text-muted'>
        Identifica empresas que mantêm saldo em disponibilidades (Caixa/Bancos) superior ao passivo oneroso
        (Empréstimos/Financiamentos), indicando possível ineficiência de tesouraria ou dedutibilidade indevida
        de despesas financeiras (RIR/2018, Art. 367).
      </p>

      {temAviso && (
        <div className='alert alert-warning mb-4'>
          <strong>AVISO DE AUDITORIA: Ineficiência Financeira Detectada</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}

      {/* KPIs */}
      <div className='auditoria-kpis mb-4' style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-alerta' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado (34%)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          <div className='auditoria-kpi-help'>IRPJ 25% + CSLL 9% sobre Juros</div>
        </div>
        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-alerta' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Juros Estimados (SELIC)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          <div className='auditoria-kpi-help'>BCB Série 4189 sobre Excesso de Caixa</div>
        </div>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Empresas com Aviso</div>
          <div className='auditoria-kpi-value' style={{ color: temAviso ? '#92400e' : undefined }}>
            {resultado.totalAvisos}
          </div>
          <div className='auditoria-kpi-help'>Caixa superior à dívida onerosa</div>
        </div>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value' style={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
            Lei 6.404/76 · RIR/2018 Art.367 · CPC 00
          </div>
        </div>
      </div>

      {/* Tabela */}
      {resultado.itens.length === 0 ? (
        <div className='auditoria-empty-state'>
          <p>
            Nenhuma situação de ineficiência financeira (Cash-Rich com Dívida Cara) identificada
            para o período selecionado. O saldo de disponibilidades não supera o passivo oneroso.
          </p>
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-hover align-middle'>
            <thead className='table-light'>
              <tr>
                <th>Empresa</th>
                <th>Fechamento</th>
                <th className='text-end'>Caixa + Bancos</th>
                <th className='text-end'>Empréstimos</th>
                <th className='text-end'>Excesso de Caixa</th>
                <th className='text-end'>% Ociosidade</th>
                <th className='text-end'>Juros Estimados</th>
                <th className='text-end'>Impacto Fiscal (34%)</th>
              </tr>
            </thead>
            <tbody>
              {resultado.itens.map((item, idx) => (
                <ItemRow key={`${item.empresaId}-${idx}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fundamentação legal */}
      {resultado.fundamentacaoLegal && (
        <details className='mt-4'>
          <summary className='text-muted' style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
            Fundamentação Legal
          </summary>
          <div className='mt-2 p-3' style={{ backgroundColor: '#f8fafc', fontSize: '0.8rem', lineHeight: 1.6 }}>
            {resultado.fundamentacaoLegal}
          </div>
        </details>
      )}
    </div>
  );
};

export default AuditoriaCashRichDetalhe;
